// What People and the person page read, as the staff member, through row level security and the staff
// reads of docs/data-model.md 10.4.2. Nothing here writes: the writes go through src/admin/api.js by
// their contract names, from the screens. Which rows come back is the database's decision by role
// (owner and admin everything, support no billing or notes, a host only their product's), so each
// read that a role can't make answers with no rows and the screen leaves that part out.
import {
  PERSON_COLUMNS, ACCESS_COLUMNS, BILLING_COLUMNS, PEOPLE_BILLING_COLUMNS, CATALOGUE_COLUMNS, COHORT_COLUMNS,
  HOLD_COLUMNS, NOTE_COLUMNS, NAME_COLUMNS, ACTIVITY_COLUMNS, REPLAY_COLUMNS, WEBINAR_COLUMNS, SPACE_COLUMNS,
  PREVIOUS_EMAIL_COLUMNS, REQUEST_COLUMNS, SCOPE_COLUMNS, START_HERE_STEP_COLUMNS, START_HERE_PROGRESS_COLUMNS,
  START_HERE_ANSWER_SCREEN_COLUMNS, START_HERE_ANSWER_COLUMNS, PAGE,
} from './columns.js'

import { startHereByProduct, startHereAnswersByProduct, holdsAnswers } from './words.js'

const OA = role => role === 'owner' || role === 'admin'

// A read the role can't make shouldn't stop the page: it answers with nothing
const soft = p => p.catch(() => [])

// 1 page of the People list (hub.staff_people), searched and filtered on the server
export function listPeople(api, { search, product, status, free } = {}, cursor = null, limit = PAGE) {
  return api.staff_people({
    search: search && search.trim() ? search.trim() : undefined,
    product: product || undefined,
    status_group: status || undefined,
    used_free: free || undefined,
    after_name: cursor?.sort_name,
    after_id: cursor?.person_id,
    limit,
  })
}

// The catalogue as the person page needs it (certificates, terms lines), and every product's intakes
export async function loadCatalogue(api) {
  const [catalogue, cohorts] = await Promise.all([
    api.run(api.from('catalogue').select(CATALOGUE_COLUMNS).order('access_kind', { ascending: false }).order('sort_order')),
    soft(api.run(api.from('cohorts').select(COHORT_COLUMNS).order('product_id').order('sort_order').order('starts_on', { nullsFirst: false }))),
  ])
  return { catalogue, cohorts }
}

// Everything the person page shows about 1 person. Null when they aren't someone the caller may read.
export async function loadPerson(api, id, role) {
  const oa = OA(role)
  const people = await api.run(api.from('people').select(PERSON_COLUMNS).eq('id', id))
  const person = people[0] || null
  if (!person) return null
  const empty = Promise.resolve([])
  const [access, outside, holds, notes, activity, replays, spaces, previous, requests, scopes, progress, history, peopleBilling, base, registrations] = await Promise.all([
    api.run(api.from('access').select(ACCESS_COLUMNS).eq('person_id', id).order('starts_at', { ascending: false }).order('id')),
    soft(api.people_outside_hub({ person: id })),
    role === 'host' ? empty : soft(api.run(api.from('free_holds').select(HOLD_COLUMNS).eq('person_id', id).is('released_at', null))),
    oa ? soft(api.run(api.from('person_notes').select(NOTE_COLUMNS).eq('person_id', id).order('created_at', { ascending: false }))) : empty,
    role === 'host' ? empty : soft(api.run(api.from('activity').select(ACTIVITY_COLUMNS).eq('person_id', id).order('occurred_at', { ascending: false }).limit(200))),
    // the free webinar replays they've watched: the hub's own record (Adam, 5 Oct, stage 6), which only
    // owner and admin may read
    oa ? soft(api.run(api.from('replay_progress').select(REPLAY_COLUMNS).eq('person_id', id).not('watched_at', 'is', null).order('watched_at', { ascending: false }))) : empty,
    oa ? soft(api.run(api.from('client_spaces').select(SPACE_COLUMNS).eq('client_person_id', id).order('opened_at', { ascending: false }))) : empty,
    oa ? soft(api.run(api.from('previous_emails').select(PREVIOUS_EMAIL_COLUMNS).eq('person_id', id).order('changed_at', { ascending: false }))) : empty,
    oa ? soft(api.run(api.from('data_requests').select(REQUEST_COLUMNS).eq('person_id', id).eq('status', 'open').order('requested_at', { ascending: false }))) : empty,
    role === 'owner' && person.role === 'host' ? soft(api.run(api.from('staff_scopes').select(SCOPE_COLUMNS).eq('person_id', id))) : empty,
    soft(api.person_progress({ person: id })),
    soft(api.person_history({ person: id, limit: 100 })),
    oa ? soft(api.run(api.from('people_billing').select(PEOPLE_BILLING_COLUMNS).eq('person_id', id))) : empty,
    loadCatalogue(api),
    // the replays they've registered for on a register page (owner and admin: it reads emails)
    oa ? soft(personRegistrations(api, person)) : empty,
  ])
  const accessIds = access.map(a => a.id)
  const webinarIds = [...new Set([...activity, ...replays, ...registrations].map(a => a.webinar_id).filter(Boolean))].sort()
  const authorIds = [...new Set(notes.map(n => n.author_id).filter(Boolean))].sort()
  // a live course with Start here rows (Profit On Your Terms): the rows and screens, then the person's
  // progress on them, for Progress's line (a role that can't read them gets none, and the line says
  // "<n> of <m> steps done." as before)
  const startHereIds = progress.filter(p => p.product_type === 'live_cohort' && p.steps_total > 0).map(p => p.product_id).sort()
  const [billing, webinars, authors, steps] = await Promise.all([
    oa && accessIds.length ? soft(api.run(api.from('access_billing').select(BILLING_COLUMNS).in('access_id', [...accessIds].sort()))) : empty,
    webinarIds.length ? soft(api.run(api.from('webinars').select(WEBINAR_COLUMNS).in('id', webinarIds))) : empty,
    authorIds.length ? soft(api.run(api.from('people').select(NAME_COLUMNS).in('id', authorIds))) : empty,
    // a host reads no one's step progress (data-model 10.6.6), so a host's line stays the count
    // person_progress gives ("1 of 2 steps done.") rather than reading every row as not started
    startHereIds.length && role !== 'host' ? soft(api.run(api.from('steps').select(START_HERE_STEP_COLUMNS).in('product_id', startHereIds)
      .in('step_type', ['screens', 'screen']).eq('is_published', true).order('product_id').order('sort_order').order('id'))) : empty,
  ])
  const stepIds = steps.map(st => st.id).sort()
  // and, for owner, admin and support, the screens' config for the Start here answers under the line
  // (Adam, 7 Oct, the batch's question 2); a host reads none of the answers, so isn't asked
  const answersToo = (oa || role === 'support') && startHereIds.length > 0
  const [stepProgress, answerScreens] = await Promise.all([
    stepIds.length ? soft(api.run(api.from('step_progress').select(START_HERE_PROGRESS_COLUMNS).eq('person_id', id).in('step_id', stepIds))) : empty,
    answersToo ? soft(api.run(api.from('steps').select(START_HERE_ANSWER_SCREEN_COLUMNS).in('product_id', startHereIds)
      .eq('step_type', 'screen').eq('is_published', true).order('product_id').order('sort_order').order('id'))) : empty,
  ])
  // the person's answers on the screens that hold a tick or a choice: the gate, the second channel and
  // each assignment
  const answerIds = answerScreens.filter(holdsAnswers).map(st => st.id).sort()
  const stepAnswers = answerIds.length
    ? await soft(api.run(api.from('step_progress').select(START_HERE_ANSWER_COLUMNS).eq('person_id', id).in('step_id', answerIds)))
    : []
  const startHere = startHereByProduct(steps, stepProgress)
  return {
    person, access, outside, holds, notes, activity, replays, registrations, spaces, previous, requests,
    scopes: scopes.map(s => s.product_id), progress, history, billing, webinars, authors,
    startHere,
    startHereAnswers: answersToo ? startHereAnswersByProduct(steps, answerScreens, stepAnswers, startHere) : {},
    hasCustomer: Boolean(peopleBilling[0]?.stripe_customer_id),
    catalogue: base.catalogue, cohorts: base.cohorts,
  }
}

// A person's registrations on the replays' register pages (docs/replay-register-spec.md 5), each with its
// webinar_id, earliest first: hub.person_replay_registrations, 1 read for the person (owner and admin
// only, as it shows emails; it answers newest first).
export async function personRegistrations(api, person) {
  const rows = await api.person_replay_registrations({ person: person.id })
  return (rows || []).slice().sort((a, b) => String(a.registered_at).localeCompare(String(b.registered_at)))
}

// The next page of History (person_history, newest first, before the last row's time)
export function olderHistory(api, id, before) {
  return api.person_history({ person: id, before, limit: 100 })
}

// The ids stripe-prices turns into amounts as the boards write them (owner and admin only, at most 50)
export function amountIds(billing, history = []) {
  const checkouts = new Set(), prices = new Set(), invoices = new Set(), charges = new Set()
  for (const b of billing || []) {
    if (b.stripe_checkout_session_id) checkouts.add(b.stripe_checkout_session_id)
    if (b.stripe_subscription_id && b.stripe_price_id) prices.add(b.stripe_price_id)
  }
  for (const h of history || []) {
    const d = h.detail || {}
    if ((h.source === 'stripe_event' && h.event === 'invoice.payment_failed') || (h.source === 'payment' && h.event === 'paid')) {
      if (d.invoice_id) invoices.add(d.invoice_id)
    }
    if (h.source === 'stripe_event' && h.event === 'charge.refunded' && d.charge_id) charges.add(d.charge_id)
  }
  const take = (set, room) => [...set].sort().slice(0, room)
  const body = { action: 'amounts' }
  let room = 50
  for (const [key, set] of [['checkout_session_ids', checkouts], ['price_ids', prices], ['invoice_ids', invoices], ['charge_ids', charges]]) {
    const ids = take(set, room)
    if (ids.length) { body[key] = ids; room -= ids.length }
  }
  return Object.keys(body).length > 1 ? body : null
}

// The amounts, or nothing: an amount that can't be read leaves the words without it
export async function loadAmounts(api, body) {
  if (!body) return {}
  try {
    return await api.stripe_prices(body)
  } catch {
    return {}
  }
}

// The paid products a grant can give (check_grant_product): in the hub, not the 1-2-1, not archived
export const grantable = p => p.access_kind === 'paid' && p.type !== 'client_space' && p.moved_in && p.state !== 'archived'

// A product's intakes, newest first, and the 1 a grant starts in by default: a product that releases
// by its own intake (Profit On Your Terms, and The Funding Engine since Adam's 10 Oct) starts in the
// running or next intake, or none when no intake is running or to come; any other product starts in none
export function cohortsOf(cohorts, productId) {
  return (cohorts || []).filter(c => c.product_id === productId)
}
export function defaultCohort(product, cohorts, todayDay) {
  if (!product || product.release_basis !== 'own_intake') return ''
  const list = cohortsOf(cohorts, product.id)
  const open = list.filter(c => !c.ends_on || c.ends_on >= todayDay).sort((a, b) => String(a.starts_on || '9999').localeCompare(String(b.starts_on || '9999')))
  return open[0]?.id || ''
}
